import { useState } from "react";
import type { ReactNode } from "react";
import type { OnboardingSetupSnapshot } from "../../onboarding";
import type { BookingPolicySnapshot } from "../domain/bookingPolicy";
import { SettingsIcon, type SettingsIconName } from "./SettingsIcon";
type BookingPolicyState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "success"; policy: BookingPolicySnapshot; saved: boolean }
  | { status: "saving"; policy: BookingPolicySnapshot }
  | { status: "error"; title: string; message: string; retryable: boolean; policy?: BookingPolicySnapshot };
type SettingsScreenId =
  | "establishment"
  | "booking-link"
  | "agenda"
  | "services"
  | "team"
  | "account"
  | "security"
  | "privacy"
  | "plan"
  | "invoices"
  | "payments"
  | "whatsapp";

type SettingsMenuItem = {
  id: SettingsScreenId;
  title: string;
  subtitle: string;
  icon: SettingsIconName;
  badge?: string;
  tone?: "teal" | "blue" | "amber" | "purple" | "coral" | "gray";
};

type SettingsCategoryId = "operation" | "account" | "plan" | "integrations";

type SettingsGroup = {
  id: SettingsCategoryId;
  title: string;
  description: string;
  icon: SettingsIconName;
  items: SettingsMenuItem[];
};

export function SaasSettingsView({ bookingPolicyState, setup }: { bookingPolicyState: BookingPolicyState; setup?: OnboardingSetupSnapshot }) {
  const status = bookingPolicyState.status === "success" ? "Atual" : setup ? "Configurado" : "Protótipo";
  const settingsGroups: SettingsGroup[] = [
    {
      id: "operation",
      title: "Operação",
      description: "Agenda, serviços e equipe",
      icon: "store",
      items: [
        { id: "establishment", title: "Estabelecimento", subtitle: "Nome, contato e endereço", icon: "store", tone: "teal" },
        { id: "booking-link", title: "Link de agendamento", subtitle: "Slug público e preview", icon: "link", tone: "blue" },
        { id: "agenda", title: "Agenda", subtitle: "Horários e confirmação", icon: "calendar", tone: "teal" },
        { id: "services", title: "Serviços", subtitle: "Catálogo e duração", icon: "services", tone: "purple" },
        { id: "team", title: "Equipe", subtitle: "Profissionais e acessos", icon: "team", tone: "blue" }
      ]
    },
    {
      id: "account",
      title: "Conta",
      description: "Perfil, segurança e privacidade",
      icon: "user",
      items: [
        { id: "account", title: "Conta", subtitle: "Usuário e sessão", icon: "user", tone: "purple" },
        { id: "security", title: "Segurança", subtitle: "Senha e proteção", icon: "shield", tone: "gray" },
        { id: "privacy", title: "Privacidade", subtitle: "Dados e consentimento", icon: "eye", tone: "gray" }
      ]
    },
    {
      id: "plan",
      title: "Plano",
      description: "Assinatura e cobrança",
      icon: "star",
      items: [
        { id: "plan", title: "Plano atual", subtitle: "Uso e recursos", icon: "star", tone: "amber" },
        { id: "invoices", title: "Faturas", subtitle: "Histórico e próximas cobranças", icon: "receipt", badge: "1", tone: "amber" },
        { id: "payments", title: "Pagamentos", subtitle: "Método de cobrança", icon: "card", tone: "amber" }
      ]
    },
    {
      id: "integrations",
      title: "Integrações",
      description: "Canais conectados",
      icon: "plug",
      items: [
        { id: "whatsapp", title: "WhatsApp", subtitle: "Conexão e campanhas", icon: "plug", tone: "gray" }
      ]
    }
  ];
  const [activeCategoryId, setActiveCategoryId] = useState<SettingsCategoryId>("operation");
  const [activeSettingsScreen, setActiveSettingsScreen] = useState<SettingsScreenId | null>("establishment");
  const activeGroup = settingsGroups.find((group) => group.id === activeCategoryId) ?? settingsGroups[0];
  const activeItem = settingsGroups.flatMap((group) => group.items).find((item) => item.id === activeSettingsScreen);

  function selectCategory(categoryId: SettingsCategoryId) {
    setActiveCategoryId(categoryId);
    setActiveSettingsScreen(null);
  }

  return (
    <div className="authshell__saas authshell__saas--settings" aria-label="Ajustes">
      <section className="authshell__settingsDesktop">
        <header className="authshell__settingsHeader">
          <div>
            <h2>Ajustes</h2>
            <p>Gerencie sua operação, conta, plano e integrações.</p>
          </div>
        </header>
        <SettingsPolicyStateNotice state={bookingPolicyState} />

        <div className="authshell__settingsCategoryGrid" role="tablist" aria-label="Categorias de ajustes">
          {settingsGroups.map((group) => (
            <button
              type="button"
              key={group.id}
              className={group.id === activeGroup.id ? "is-active" : ""}
              onClick={() => selectCategory(group.id)}
              role="tab"
              aria-selected={group.id === activeGroup.id}
            >
              <span aria-hidden="true">
                <SettingsIcon name={group.icon} />
              </span>
              <strong>{group.title}</strong>
              <small>{group.items.length} itens</small>
            </button>
          ))}
        </div>

        <div className="authshell__settingsSplit">
          <section className="authshell__settingsList" aria-label={`Ajustes de ${activeGroup.title}`}>
            <div>
              <p>{activeGroup.title}</p>
              <span>{activeGroup.description}</span>
            </div>
            <div className="authshell__settingsItemGrid">
              {activeGroup.items.map((item) => (
                <button
                  type="button"
                  key={item.id}
                  className={item.id === activeSettingsScreen ? "is-active" : ""}
                  onClick={() => setActiveSettingsScreen(item.id)}
                  aria-pressed={item.id === activeSettingsScreen}
                >
                  <span className={`authshell__settingsItemIcon authshell__settingsItemIcon--${item.tone ?? "gray"}`} aria-hidden="true">
                    <SettingsIcon name={item.icon} />
                  </span>
                  <span>
                    <strong>{item.title}</strong>
                    <small>{item.subtitle}</small>
                  </span>
                  {item.badge && <em>{item.badge}</em>}
                  <b>Editar</b>
                </button>
              ))}
            </div>
          </section>

          <aside className={`authshell__settingsSidePanel ${activeItem ? "" : "is-closed"}`} aria-label="Detalhe do ajuste">
            <div className="authshell__settingsSidePanelInner">
              {activeItem ? (
                <>
                  <div className="authshell__settingsSideTopbar">
                    <button type="button" onClick={() => setActiveSettingsScreen(null)} aria-label="Fechar detalhe">
                      ×
                    </button>
                    <div>
                      <strong>{activeItem.title}</strong>
                      <span>{activeItem.subtitle}</span>
                    </div>
                    <em>{status}</em>
                  </div>
                  <SettingsDetailScreen screen={activeItem.id} status={status} setup={setup} />
                </>
              ) : null}
            </div>
          </aside>
        </div>
      </section>

      <div className="authshell__settingsMobile" aria-label="Ajustes mobile">
        <SettingsPolicyStateNotice state={bookingPolicyState} />
        {activeItem ? (
          <section className="authshell__settingsMobileDetail" aria-label={`Detalhe de ${activeItem.title}`}>
            <div className="authshell__settingsMobileDetailTopbar">
              <button type="button" onClick={() => setActiveSettingsScreen(null)} aria-label="Voltar para lista de ajustes">
                <span aria-hidden="true">‹</span>
                Voltar
              </button>
              <div>
                <strong>{activeItem.title}</strong>
                <small>{activeItem.subtitle}</small>
              </div>
              <em>{status}</em>
            </div>
            <SettingsDetailScreen screen={activeItem.id} status={status} setup={setup} />
          </section>
        ) : (
          <>
            <button className="authshell__settingsMobileAccount" type="button" onClick={() => setActiveSettingsScreen("account")}>
              <span>AD</span>
              <div>
                <strong>Admin</strong>
                <small>admin@thh.local</small>
              </div>
              <SettingsIcon name="chevron" />
            </button>
            {settingsGroups.map((group) => (
              <section className="authshell__settingsMobileGroup" key={`mobile-${group.title}`}>
                <p>{group.title === "Operação" ? "Geral" : group.title}</p>
                <div>
                  {group.items.map((item) => (
                    <button type="button" key={item.id} onClick={() => setActiveSettingsScreen(item.id)}>
                      <span className={`authshell__settingsMobileIcon authshell__settingsMobileIcon--${item.tone ?? "gray"}`} aria-hidden="true">
                        <SettingsIcon name={item.icon} />
                      </span>
                      <span>
                        <strong>{item.title}</strong>
                        <small>{item.subtitle}</small>
                      </span>
                      {item.badge && <em>{item.badge}</em>}
                      <SettingsIcon name="chevron" />
                    </button>
                  ))}
                </div>
              </section>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

function SettingsPolicyStateNotice({ state }: { state: BookingPolicyState }) {
  if (state.status === "idle" || state.status === "loading") {
    return (
      <div className="authshell__inlineStatus" role="status">
        <strong>Sincronizando ajustes</strong>
        <span>Buscando a política operacional salva no backend.</span>
      </div>
    );
  }

  if (state.status !== "error") {
    return null;
  }

  return (
    <div className="authshell__inlineerror" role="alert">
      <strong>{state.title}</strong>
      <span>{state.message}</span>
    </div>
  );
}

function SettingsDetailScreen({ screen, status, setup }: { screen: SettingsScreenId; status: string; setup?: OnboardingSetupSnapshot }) {
  if (screen === "establishment") {
    const establishmentStatus = status === "Atual" || status === "Configurado" ? "Salvo" : status;
    return (
      <SettingsPanel title="Estabelecimento" description="Dados que aparecem para clientes e dentro da operação." status={establishmentStatus}>
        <div className="authshell__settingsFormGrid">
          <SettingsField label="Nome do estabelecimento" value="THH Staging" />
          <SettingsField label="Telefone principal" value="(11) 90000-0000" />
          <SettingsField label="Endereço" value="Rua das Flores, 120" wide />
          <SettingsField label="Cidade" value="São Paulo" />
          <SettingsField label="Fuso horário" value={setup?.timezone ?? "America/Sao_Paulo"} />
        </div>
        <SettingsActionRow primary="Salvar estabelecimento" secondary="Validar duplicidade do nome" />
      </SettingsPanel>
    );
  }

  if (screen === "booking-link") {
    return (
      <SettingsPanel title="Link de agendamento" description="Endereço público usado pelos clientes para agendar." status="Protótipo">
        <div className="authshell__settingsLinkBuilder">
          <span>thh.go/</span>
          <input defaultValue="thh-staging" aria-label="Slug do link de agendamento" />
        </div>
        <div className="authshell__settingsPreview">
          <small>Preview</small>
          <strong>https://thh.go/thh-staging</strong>
          <button type="button">Copiar</button>
        </div>
        <p className="authshell__settingsNote">Quando o nome ou slug já existir, o sistema deve bloquear o salvamento e pedir uma correção.</p>
        <SettingsActionRow primary="Salvar link" secondary="Abrir como cliente" />
      </SettingsPanel>
    );
  }

  if (screen === "agenda") {
    return (
      <SettingsPanel title="Agenda" description="Regras básicas de disponibilidade e confirmação." status={status}>
        <div className="authshell__settingsFormGrid">
          <SettingsSelect label="Intervalo dos horários" value="30 minutos" options={["15 minutos", "30 minutos", "60 minutos"]} />
          <SettingsSelect label="Confirmação de presença" value="Obrigatória" options={["Desativada", "Opcional", "Obrigatória"]} />
          <SettingsField label="Prazo para confirmar" value="120 minutos" />
          <SettingsSelect label="Check-in de chegada" value="Opcional" options={["Desativado", "Opcional", "Obrigatório"]} />
        </div>
        <SettingsActionRow primary="Salvar agenda" secondary="Ver política atual" />
      </SettingsPanel>
    );
  }

  if (screen === "services") {
    return (
      <SettingsPanel title="Serviços" description="Catálogo que alimenta a agenda pública." status="Protótipo">
        <SettingsList rows={[
          ["Corte Feminino", "60 min", "R$ 120"],
          ["Barba Completa", "45 min", "R$ 70"],
          ["Coloração", "90 min", "R$ 180"]
        ]} />
        <SettingsActionRow primary="Novo serviço" secondary="Organizar catálogo" />
      </SettingsPanel>
    );
  }

  if (screen === "team") {
    return (
      <SettingsPanel title="Equipe" description="Profissionais, papéis e acesso ao tenant." status="Protótipo">
        <SettingsList rows={[
          ["Ana Lima", "Profissional", "Agenda ativa"],
          ["Carlos Mendes", "Profissional", "Agenda ativa"],
          ["Admin", "Proprietário", "Acesso total"]
        ]} />
        <SettingsActionRow primary="Convidar pessoa" secondary="Revisar permissões" />
      </SettingsPanel>
    );
  }

  if (screen === "account") {
    return (
      <SettingsPanel title="Conta" description="Perfil do usuário logado e preferências de sessão." status="Atual">
        <div className="authshell__settingsFormGrid">
          <SettingsField label="Nome" value="Admin" />
          <SettingsField label="E-mail" value="admin@thh.local" />
          <SettingsSelect label="Idioma" value="Português" options={["Português"]} />
          <SettingsSelect label="Notificações por e-mail" value="Ativas" options={["Ativas", "Pausadas"]} />
        </div>
        <SettingsActionRow primary="Salvar conta" secondary="Encerrar sessões" />
      </SettingsPanel>
    );
  }

  if (screen === "security") {
    return (
      <SettingsPanel title="Segurança" description="Senha, sessão e proteção da conta." status="Protótipo">
        <SettingsList rows={[
          ["Senha", "Alteração protegida", "Pendente contrato"],
          ["Autenticação em duas etapas", "Recomendado", "Em desenho"],
          ["Sessões ativas", "Este dispositivo", "Atual"]
        ]} />
        <SettingsActionRow primary="Alterar senha" secondary="Ver sessões" />
      </SettingsPanel>
    );
  }

  if (screen === "privacy") {
    return (
      <SettingsPanel title="Privacidade" description="Preferências de dados, retenção e consentimento." status="Protótipo">
        <div className="authshell__settingsToggleList">
          <SettingsToggle title="Solicitar consentimento no agendamento" detail="Cliente confirma uso dos dados antes de enviar." />
          <SettingsToggle title="Ocultar dados sensíveis em listas" detail="Reduz exposição em telas compartilhadas." />
          <SettingsToggle title="Permitir exportação do tenant" detail="Disponível apenas para proprietários." />
        </div>
        <SettingsActionRow primary="Salvar privacidade" secondary="Exportar dados" />
      </SettingsPanel>
    );
  }

  if (screen === "plan") {
    return (
      <SettingsPanel title="Plano atual" description="Resumo do plano, uso e recursos ativos." status="Atual">
        <SettingsList rows={[
          ["Plano", "THH GO Staging", "Ativo"],
          ["Agendamentos", "12 neste mês", "Dentro do limite"],
          ["WhatsApp", "Não conectado", "Configurar"]
        ]} />
        <SettingsActionRow primary="Alterar plano" secondary="Ver limites" />
      </SettingsPanel>
    );
  }

  if (screen === "invoices") {
    return (
      <SettingsPanel title="Faturas" description="Histórico financeiro e próximas cobranças." status="Protótipo">
        <SettingsList rows={[
          ["Próxima fatura", "01/06/2026", "Prevista"],
          ["Maio 2026", "R$ 0,00", "Ambiente staging"],
          ["Abril 2026", "R$ 0,00", "Ambiente staging"]
        ]} />
        <SettingsActionRow primary="Abrir faturas" secondary="Baixar histórico" />
      </SettingsPanel>
    );
  }

  if (screen === "payments") {
    return (
      <SettingsPanel title="Pagamentos" description="Método de cobrança e dados fiscais." status="Protótipo">
        <div className="authshell__settingsFormGrid">
          <SettingsSelect label="Método de pagamento" value="Não configurado" options={["Não configurado", "Cartão", "Pix", "Boleto"]} />
          <SettingsField label="Documento fiscal" value="00.000.000/0001-00" />
          <SettingsField label="E-mail financeiro" value="financeiro@thh.local" wide />
        </div>
        <SettingsActionRow primary="Salvar pagamento" secondary="Solicitar suporte" />
      </SettingsPanel>
    );
  }

  return (
    <SettingsPanel title="WhatsApp" description="Conexão usada para lembretes, confirmações e campanhas." status="Desconectado">
      <div className="authshell__settingsWhatsapp">
        <span aria-hidden="true">
          <SettingsIcon name="plug" />
        </span>
        <div>
          <strong>Conecte seu WhatsApp</strong>
          <p>Use um número exclusivo do negócio para automações e lembretes.</p>
        </div>
      </div>
      <SettingsActionRow primary="Conectar agora" secondary="Ver requisitos" />
    </SettingsPanel>
  );
}

function SettingsPanel({ title, description, status, children }: { title: string; description: string; status: string; children: ReactNode }) {
  return (
    <section className="authshell__settingsDetail" aria-live="polite">
      <header>
        <div>
          <h3>{title}</h3>
          <p>{description}</p>
        </div>
        <span>{status}</span>
      </header>
      {children}
    </section>
  );
}

function SettingsField({ label, value, wide }: { label: string; value: string; wide?: boolean }) {
  return (
    <label className={wide ? "authshell__settingsWide" : ""}>
      <span>{label}</span>
      <input defaultValue={value} />
    </label>
  );
}

function SettingsSelect({ label, value, options }: { label: string; value: string; options: string[] }) {
  return (
    <label>
      <span>{label}</span>
      <select defaultValue={value}>
        {options.map((option) => <option key={option}>{option}</option>)}
      </select>
    </label>
  );
}

function SettingsList({ rows }: { rows: Array<[string, string, string]> }) {
  return (
    <div className="authshell__settingsTable">
      {rows.map(([title, detail, status]) => (
        <article key={`${title}-${detail}`}>
          <strong>{title}</strong>
          <span>{detail}</span>
          <em>{status}</em>
        </article>
      ))}
    </div>
  );
}

function SettingsToggle({ title, detail }: { title: string; detail: string }) {
  return (
    <label className="authshell__settingsToggle">
      <input type="checkbox" defaultChecked />
      <span>
        <strong>{title}</strong>
        <small>{detail}</small>
      </span>
    </label>
  );
}

function SettingsActionRow({ primary, secondary }: { primary: string; secondary: string }) {
  return (
    <footer className="authshell__settingsActions">
      <button type="button">{secondary}</button>
      <button type="button">{primary}</button>
    </footer>
  );
}
